import {
    AppBar,
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Tab,
    Tabs,
    Toolbar,
    Tooltip,
} from '@mui/material';

import {
    Logout,
    UserMenu,
    useUserMenu,
    ToggleThemeButton,
    useTheme,
} from 'react-admin';
import type { ReactNode } from 'react';
import { AdminMenu } from '../admin/AdminMenu';
import { NotificationBell } from '../../notifications/NotificationBell';
import { NotificationToaster } from '../../notifications/NotificationToaster';
import { alpha } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import {
    useConfigurationContext,
    useThemeLogo,
} from '../../root/ConfigurationContext';
import { SearchBar } from '../markets/components/SearchBar';
import { useAccessFeatures, canUseFeature } from '../../auth/useAccessFeatures';
import { activeSectionId } from './navSections';
import { useNavSections } from './useNavSections';
import type { MenuEntry } from './menuEntries';
import { useMenuEntries } from './useMenuEntries';
import { GUTTER_PADDING, padRight } from './chrome';
import {
    AvatarDisc,
    BellIcon,
    MoonIcon,
    SunIcon,
    TAB_ICONS,
} from './mobile/icons';

interface HeaderProps {
    /** 'condensed' (900-1199 px): search as an icon, tabs in the flow. */
    variant?: 'full' | 'condensed';
}

// The condensed header's own theme button: the outline moon of the mockup
// (react-admin's ToggleThemeButton has the filled Material icons).
const CondensedThemeButton = () => {
    const [theme, setTheme] = useTheme();
    const Icon = theme === 'dark' ? SunIcon : MoonIcon;
    return (
        // The same tooltip react-admin's ToggleThemeButton has, with its
        // 300 ms hover delay.
        <Tooltip title="Toggle light/dark mode" enterDelay={300}>
            <IconButton
                color="inherit"
                aria-label="Toggle light/dark mode"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                sx={t => ({ color: alpha(t.palette.text.primary, 0.7) })}
            >
                <Icon sx={{ fontSize: 22 }} />
            </IconButton>
        </Tooltip>
    );
};

const Header = ({ variant = 'full' }: HeaderProps) => {
    const condensed = variant === 'condensed';
    const [theme] = useTheme();
    const { title } = useConfigurationContext();
    const location = useLocation();
    // Shared with RequireAccess and DefaultLanding so the tabs can never
    // disagree with the route guard about what the user may see.
    const access = useAccessFeatures();

    // Advanced search is gated on 'events' (see ROUTE_ACCESS). Hiding the
    // icon for anyone else keeps a visible control from landing them on
    // /no-access. canUseFeature, not includes(), so the admin bypass and
    // the pending state are handled the same way the route guard does.
    const canUseAdvancedSearch = canUseFeature(access, 'events');

    const sections = useNavSections();
    const activeId = activeSectionId(sections, location.pathname);
    // Tabs must never be handed the value of a tab that is not rendered.
    const currentPath: string | false =
        sections.find(s => s.id === activeId)?.to ?? false;
    const mainEntries = useMenuEntries().filter(e => e.group === 'main');

    const logo = useThemeLogo();

    // The search box: next to the logo, or (condensed) at the right end.
    const searchBox = (
        <Box sx={{ ml: condensed ? 0 : 2, width: condensed ? 44 : 320 }}>
            <SearchBar
                compact
                collapsible={condensed}
                anchor={condensed ? 'right' : undefined}
                outlineIcons={condensed}
                advancedHref={
                    canUseAdvancedSearch ? '/markets/search' : undefined
                }
            />
        </Box>
    );

    const userMenu = (icon?: ReactNode) => (
        <UserMenu {...(icon ? { icon } : {})}>
            {mainEntries.map(entry => (
                <UserMenuEntry key={entry.id} entry={entry} />
            ))}
            <AdminMenu />
            <Logout />
        </UserMenu>
    );

    return (
        <AppBar position="fixed" color="primary" elevation={1}>
            <Toolbar
                variant="dense"
                sx={{
                    // The toolbar's own gutters plus the notch inset of a
                    // landscape phone (0 on desktops: no change there).
                    ...GUTTER_PADDING,
                    // Condensed: the 60 px of the mockup (the dense toolbar is
                    // 48) and 12 px at the right, where the icons end.
                    ...(condensed
                        ? {
                              minHeight: 60,
                              pr: {
                                  xs: padRight(12),
                                  sm: padRight(12),
                              },
                          }
                        : {}),
                    position: 'relative', // IMPORTANT
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    component={Link}
                    to="/"
                    sx={{ color: 'inherit', textDecoration: 'inherit' }}
                >
                    <Box
                        component="img"
                        sx={{ height: 24 }}
                        src={logo}
                        alt={title}
                    />
                </Box>

                {/* Search bar next to logo; in the condensed header its icon
                    is the first of the right group instead (below). */}
                {!condensed && searchBox}

                <Tabs
                    value={currentPath}
                    aria-label="Navigation Tabs"
                    indicatorColor="secondary"
                    textColor="inherit"
                    sx={
                        condensed
                            ? {
                                  mx: 'auto',
                                  minHeight: 60,
                                  '& .MuiTab-root': { minHeight: 60 },
                              }
                            : {
                                  position: 'absolute',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                              }
                    }
                >
                    {sections.map(section => {
                        // Condensed: the outline icons of the mockup.
                        const Icon = condensed
                            ? TAB_ICONS[section.id]
                            : section.icon;
                        return (
                            <Tab
                                key={section.id}
                                label={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: condensed ? 0.75 : 0.5,
                                        }}
                                    >
                                        <Icon fontSize="small" />
                                        {section.label}
                                    </Box>
                                }
                                component={Link}
                                to={section.to}
                                value={section.to}
                            />
                        );
                    })}
                </Tabs>

                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ ml: condensed ? 0 : 'auto' }}
                >
                    {condensed && searchBox}
                    {condensed ? (
                        // 44 x 44 targets, outline icons, the grey avatar disc.
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                                '& .MuiIconButton-root': {
                                    width: 44,
                                    height: 44,
                                },
                            }}
                        >
                            <CondensedThemeButton />
                            <NotificationBell
                                icon={BellIcon}
                                dot
                                sx={t => ({
                                    color: alpha(t.palette.text.primary, 0.7),
                                })}
                            />
                            <NotificationToaster />
                            {userMenu(<AvatarDisc />)}
                        </Box>
                    ) : (
                        <>
                            <ToggleThemeButton />
                            <NotificationBell />
                            <NotificationToaster />
                            {userMenu()}
                        </>
                    )}
                </Box>
            </Toolbar>
        </AppBar>
    );
};

// One user-menu row. A component (not inline JSX) so it can read
// useUserMenu() and close the menu on click.
const UserMenuEntry = ({ entry }: { entry: MenuEntry }) => {
    const { onClose } = useUserMenu() ?? {};
    const Icon = entry.icon;
    return (
        <MenuItem component={Link} to={entry.to ?? '/'} onClick={onClose}>
            <ListItemIcon>
                <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{entry.label}</ListItemText>
        </MenuItem>
    );
};

export default Header;
